import {
  TCT_CURL,
  TCT_MURL,
  TCT_MURL_ETAG,
  TCT_MURL_IDENTITY,
  TCT_MURL_PROFILE,
  TCT_SITEMAP,
} from "../tct-demo/fixtures";
import type {
  StoryBranch,
  StoryBranchFixture,
  StoryStateValue,
  StoryValidatorRow,
  StoryValidatorSet,
} from "./types";

export { TCT_CURL, TCT_MURL, TCT_MURL_PROFILE, TCT_SITEMAP };

export const STORY_SBR = "https://example.com/api/article/123";
export const STORY_AST_CORE_PROFILE =
  "https://www.ietf.org/archive/id/draft-jurkovikj-httpapi-agentic-state-02.html#ast-core-profile";

export const STORY_BASE: StoryStateValue = Object.freeze({
  summary: "HTTP validators support efficient revalidation.",
  title: "Understanding HTTP Validators",
  workflow: "draft",
});

export const STORY_SOURCE: StoryStateValue = Object.freeze({
  summary:
    "HTTP validators support efficient revalidation and safe retries.",
  title: "Understanding HTTP Validators",
  workflow: "draft",
});

export const STORY_TARGET: StoryStateValue = Object.freeze({
  summary: "HTTP validators support efficient revalidation.",
  title: "Understanding HTTP Validators",
  workflow: "published",
});

export const STORY_MERGED: StoryStateValue = Object.freeze({
  summary:
    "HTTP validators support efficient revalidation and safe retries.",
  title: "Understanding HTTP Validators",
  workflow: "published",
});

export const STORY_CONFLICT_SOURCE: StoryStateValue = Object.freeze({
  summary: "HTTP validators support efficient revalidation.",
  title: "A Practical Validator Guide",
  workflow: "draft",
});

export const STORY_CONFLICT_TARGET: StoryStateValue = Object.freeze({
  summary: "HTTP validators support efficient revalidation.",
  title: "HTTP Validator Handbook",
  workflow: "draft",
});

export const STORY_BASE_VALIDATORS: StoryValidatorSet = Object.freeze({
  murlEtag: TCT_MURL_ETAG,
  sbrEtag: '"json-v18"',
  semanticEtag: '"article-state-v7"',
});

export const STORY_TARGET_VALIDATORS: StoryValidatorSet = Object.freeze({
  murlEtag: TCT_MURL_ETAG,
  sbrEtag: '"json-v19"',
  semanticEtag: '"article-state-v8"',
});

export const STORY_MERGED_MURL_IDENTITY =
  '{"canonical_url":"https://example.com/article/123","content":"# Overview\\n\\nHTTP validators support efficient revalidation and safe retries.","content_media_type":"text/markdown; charset=utf-8","language":"en","profile":"https://www.ietf.org/archive/id/draft-jurkovikj-collab-tunnel-03.html#tct-m-url-profile","title":"Understanding HTTP Validators"}';

export const STORY_MERGED_VALIDATORS: StoryValidatorSet = Object.freeze({
  murlEtag:
    '"sha256-7c9d1ba4a5797d4d26044cc77906b4f97595340196927028028094486fdbc07a"',
  sbrEtag: '"json-v20"',
  semanticEtag: '"article-state-v9"',
});

export const STORY_CONFLICT_TARGET_MURL_IDENTITY =
  '{"canonical_url":"https://example.com/article/123","content":"# Overview\\n\\nHTTP validators support efficient revalidation.","content_media_type":"text/markdown; charset=utf-8","language":"en","profile":"https://www.ietf.org/archive/id/draft-jurkovikj-collab-tunnel-03.html#tct-m-url-profile","title":"HTTP Validator Handbook"}';

export const STORY_CONFLICT_TARGET_VALIDATORS: StoryValidatorSet =
  Object.freeze({
    murlEtag:
      '"sha256-7df2b62c9f475fd2a9d3316187901608ae4d56be1e131c0a027d137b4e40aeb9"',
    sbrEtag: '"json-v19"',
    semanticEtag: '"article-state-v8"',
  });

export const STORY_BRANCHES: Readonly<Record<StoryBranch, StoryBranchFixture>> =
  Object.freeze({
    merge: Object.freeze({
      id: "merge",
      label: "Non-overlapping edits merge",
      summary:
        "The local summary edit and intervening workflow edit combine into one complete candidate.",
      source: STORY_SOURCE,
      target: STORY_TARGET,
      targetMurlIdentity: TCT_MURL_IDENTITY,
      targetValidators: STORY_TARGET_VALIDATORS,
      expectedStatus: "merged",
    }),
    conflict: Object.freeze({
      id: "conflict",
      label: "Concurrent title edits conflict",
      summary:
        "Both writers change the same title differently, so the operation stops at /title.",
      source: STORY_CONFLICT_SOURCE,
      target: STORY_CONFLICT_TARGET,
      targetMurlIdentity: STORY_CONFLICT_TARGET_MURL_IDENTITY,
      targetValidators: STORY_CONFLICT_TARGET_VALIDATORS,
      expectedStatus: "conflict",
    }),
  });

export const STORY_VALIDATOR_ROWS: readonly StoryValidatorRow[] = [
  {
    state: "Base",
    sbrEtag: STORY_BASE_VALIDATORS.sbrEtag ?? "none",
    murlEtag: STORY_BASE_VALIDATORS.murlEtag ?? "none",
    semanticEtag: STORY_BASE_VALIDATORS.semanticEtag ?? "none",
    meaning: "Same initial logical state; ordinary ETags remain scoped.",
  },
  {
    state: "Source",
    sbrEtag: "none",
    murlEtag: "none",
    semanticEtag: "none",
    meaning: "Local value only; no server validator is invented.",
  },
  {
    state: "Target",
    sbrEtag: STORY_TARGET_VALIDATORS.sbrEtag ?? "none",
    murlEtag: STORY_TARGET_VALIDATORS.murlEtag ?? "none",
    semanticEtag: STORY_TARGET_VALIDATORS.semanticEtag ?? "none",
    meaning:
      "Workflow changed: SBR and semantic validators advance; M-URL bytes do not.",
  },
  {
    state: "Merged",
    sbrEtag: STORY_MERGED_VALIDATORS.sbrEtag ?? "none",
    murlEtag: STORY_MERGED_VALIDATORS.murlEtag ?? "none",
    semanticEtag: STORY_MERGED_VALIDATORS.semanticEtag ?? "none",
    meaning: "Approved summary change advances both selected representations.",
  },
];

export function storyJson(value: StoryStateValue): string {
  return JSON.stringify({
    summary: value.summary,
    title: value.title,
    workflow: value.workflow,
  });
}

export function storyBranch(id: StoryBranch): StoryBranchFixture {
  return STORY_BRANCHES[id];
}

export const STORY_BASE_JSON = storyJson(STORY_BASE);

export function storyMergeInput(branch: StoryBranch) {
  const fixture = storyBranch(branch);
  return Object.freeze({
    base: STORY_BASE_JSON,
    source: storyJson(fixture.source),
    target: storyJson(fixture.target),
  });
}
